import React, { createContext, useContext, useEffect, useState } from 'react';
import { Loader } from '@googlemaps/js-api-loader';

interface MapsApiContextValue {
  isReady: boolean;
  googleNS: typeof google | null;
}

interface MapsApiProviderProps {
  apiKey: string;
  children: React.ReactNode;
}

const MapsApiContext = createContext<MapsApiContextValue>({
  isReady: false,
  googleNS: null,
});

export const useMapsApi = () => {
  const context = useContext(MapsApiContext);
  if (!context) {
    throw new Error('useMapsApi must be used within a MapsApiProvider');
  }
  return context;
};

const MapsApiProvider: React.FC<MapsApiProviderProps> = ({ apiKey, children }) => {
  const [isReady, setIsReady] = useState(false);
  const [googleNS, setGoogleNS] = useState<typeof google | null>(null);

  useEffect(() => {
    // Check if Google Maps is already loaded
    if (window.google && window.google.maps) {
      setGoogleNS(window.google);
      setIsReady(true);
      return;
    }

    const loader = new Loader({
      apiKey,
      version: 'weekly',
      language: 'en',
      libraries: ['places'],
    });

    loader.load().then(() => {
      setGoogleNS(window.google);
      setIsReady(true);
    });
  }, [apiKey]);

  const contextValue = React.useMemo(() => ({ isReady, googleNS }), [isReady, googleNS]);

  return <MapsApiContext.Provider value={contextValue}>{children}</MapsApiContext.Provider>;
};

export default MapsApiProvider;
